/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { Environment, OrbitControls, Sparkles } from '@react-three/drei';
import { VoiceAgentVisualization } from './voice-agent-visualization';
import { VoiceControls } from './voice-controls';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { MiniVoiceAgentDialog } from './mini-voice-agent-dialog';
import { useSnapshot } from 'valtio';
import { voiceStore, voiceActions } from '@/stores/voiceStore';
import { useAuth } from '@/hooks/useAuth';

// Add TypeScript declaration
declare global {
	interface Window {
		__lastPrometheusClickTime?: number;
		__hasOpenedVoiceDialog?: boolean;
	}
}

export interface VoiceAgentDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConnectionChange?: (connected: boolean) => void;
	onMinimize?: () => void;
	onMaximize?: () => void;
	showMini?: boolean;
	onVolumeChange?: (volume: number) => void;
	onSpeakingStateChange?: (isSpeaking: boolean) => void;
	isTransitioning?: boolean;
	userData?: VoiceUserData;
	minimizeDialogRef?: React.MutableRefObject<{ minimize: () => void }>;
}

interface VoiceUserData {
	email?: string;
	name?: string;
	role?: string;
	isAdmin?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

export function VoiceAgentDialog({
	isOpen,
	onClose,
	onConnectionChange,
	onMinimize,
	onMaximize,
	showMini,
	onVolumeChange,
	onSpeakingStateChange,
	isTransitioning,
	userData: propUserData,
	minimizeDialogRef,
}: VoiceAgentDialogProps) {
	const [volumeLevel, setVolumeLevel] = React.useState(0);
	const [isAnimating, setIsAnimating] = React.useState(false);
	const isTransitioningRef = React.useRef(false);
	const { user, isLoading } = useAuth();
	const email = user?.email;

	// State for client-side initialization and animation properties
	const [clientSideInitialized, setClientSideInitialized] =
		React.useState(false);
	// Default to "first open" animation (backdrop starts at 0.6 opacity)
	const [backdropInitialProps, setBackdropInitialProps] = React.useState<{
		opacity: number;
	}>({ opacity: 0.6 });

	React.useEffect(() => {
		// On the client, check if the dialog has been opened before in this session
		// This will determine the initial animation state for the backdrop
		if (typeof window !== 'undefined') {
			if (window.__hasOpenedVoiceDialog) {
				// If opened before, backdrop should start transparent and fade in
				setBackdropInitialProps({ opacity: 0 });
			} else {
				// First time, backdrop starts at full desired opacity (matching original logic)
				setBackdropInitialProps({ opacity: 0.6 });
			}
		}
		setClientSideInitialized(true); // Signal that client-side values are ready
	}, []); // Runs once on client mount

	// Initialize prevention of navigation from Prometheus button
	React.useEffect(() => {
		// Listen for the custom event from our PrometheusButton component
		const handlePrometheusButtonClick = () => {
			// console.log(
			// 	'🔍 [VoiceAgentDialog] Received prometheus-button-clicked event'
			// );

			// If dialog is already open, just ensure it's visible
			if (isOpen) {
				// console.log(
				// 	'🔍 [VoiceAgentDialog] Dialog already open, ensuring visibility'
				// );

				// Force visibility of dialog and backdrop
				setTimeout(() => {
					const dialogs = document.querySelectorAll(
						'[data-voice-dialog-container]'
					);
					dialogs.forEach((dialog) => {
						(dialog as HTMLElement).style.visibility = 'visible';
						(dialog as HTMLElement).style.opacity = '1';
						(dialog as HTMLElement).style.display = 'flex';
					});

					const backdrops = document.querySelectorAll(
						'[data-voice-dialog-backdrop]'
					);
					backdrops.forEach((backdrop) => {
						(backdrop as HTMLElement).style.visibility = 'visible';
						(backdrop as HTMLElement).style.opacity = '0.6';
						(backdrop as HTMLElement).style.display = 'block';
					});
				}, 50);
			}
		};

		document.addEventListener(
			'prometheus-button-clicked',
			handlePrometheusButtonClick
		);

		return () => {
			document.removeEventListener(
				'prometheus-button-clicked',
				handlePrometheusButtonClick
			);
		};
	}, [isOpen]);

	// Use Valtio store
	const state = useSnapshot(voiceStore);
	const prevConnectedRef = React.useRef(state.isConnected);

	// Track when connection state changes to false
	React.useEffect(() => {
		if (prevConnectedRef.current && !state.isConnected) {
			console.log(
				'❗ [VoiceAgentDialog] Connection state changed to FALSE:',
				{
					showMini,
					isOpen,
					timestamp: new Date().toISOString(),
				}
			);
		}
		prevConnectedRef.current = state.isConnected;
	}, [state.isConnected, showMini, isOpen]);

	// Add debug logs for main component rendering
	React.useEffect(() => {
		// console.log('🔍 [VoiceAgentDialog] Main render:', {
		// 	isOpen,
		// 	showMini,
		// 	isConnected: state.isConnected,
		// 	timestamp: new Date().toISOString(),
		// });
	}, [isOpen, showMini, state.isConnected]);

	// Get user data directly from Convex
	const convexUser = useQuery(api.users.getByEmail, { email: email ?? '' });

	// Format user data for voice controls
	const fetchedUserData: VoiceUserData | undefined = React.useMemo(() => {
		if (!convexUser) return undefined;
		return {
			id: convexUser._id,
			email: convexUser.email,
			name: convexUser.name,
			role: convexUser.role,
			isAdmin: convexUser.isAdmin,
		};
	}, [convexUser]);

	// Create navigation function
	const navigationStart = React.useCallback<() => Promise<void>>(async () => {
		// console.log('🎯 [VoiceAgentDialog] Navigation sequence:', {
		// 	showMini,
		// 	isConnected: state.isConnected,
		// 	isAnimating,
		// });

		try {
			// First minimize if not already minimized
			if (!showMini) {
				// console.log('🔄 [VoiceAgentDialog] Minimizing dialog');
				onMinimize?.();
				// Wait for minimize animation
				await new Promise((resolve) => setTimeout(resolve, 300));
				// console.log('✅ [VoiceAgentDialog] Minimize complete');
			}

			// Then close
			// console.log('🔄 [VoiceAgentDialog] Closing dialog');
			onClose();
			// console.log('✅ [VoiceAgentDialog] Navigation complete');
		} catch (error) {
			console.error('❌ [VoiceAgentDialog] Navigation error:', error);
		}
	}, [onMinimize, onClose, showMini]);

	// Handle connection state changes from VoiceControls
	const handleConnectionChange = React.useCallback(
		(connected: boolean) => {
			// console.log('🔌 VoiceAgentDialog - Connection state change:', {
			// 	connected,
			// 	showMini,
			// 	isOpen,
			// 	isConnected: state.isConnected,
			// 	isAnimating,
			// 	timestamp: new Date().toISOString(),
			// });

			voiceActions.setConnected(connected);
			if (connected && showMini) {
				// console.log(
				// 	'🔍 [VoiceAgentDialog] Setting assistant speaking to true'
				// );
				voiceActions.setAssistantSpeaking(true);
			}
			if (onConnectionChange) {
				onConnectionChange(connected);
			}
		},
		[onConnectionChange, showMini]
	);

	// Preserve connection state when switching between mini and full view
	React.useEffect(() => {
		// console.log('🔄 [VoiceAgentDialog] View state changed:', {
		// 	showMini,
		// 	isConnected: state.isConnected,
		// 	isOpen,
		// 	timestamp: new Date().toISOString(),
		// });
		// Log the mini dialog rendering condition
		// console.log('🔍 [VoiceAgentDialog] Mini dialog condition:', {
		// 	shouldRenderMini: showMini && isOpen && state.isConnected,
		// 	showMini,
		// 	isOpen,
		// 	isConnected: state.isConnected,
		// 	timestamp: new Date().toISOString(),
		// });
	}, [showMini, state.isConnected, isOpen]);

	// Handle minimizing
	const handleMinimize = React.useCallback<() => void>(() => {
		// console.log('🔄 [VoiceAgentDialog] Minimize requested:', {
		// 	isConnected: state.isConnected,
		// 	showMini,
		// });

		if (!showMini) {
			onMinimize?.();
			// console.log('✅ [VoiceAgentDialog] Minimize initiated');
		}
	}, [onMinimize, showMini]);

	// Handle closing
	const handleClose = React.useCallback(() => {
		// console.log('🔄 [VoiceAgentDialog] Close requested:', {
		// 	showMini,
		// 	isConnected: state.isConnected,
		// 	isOpen,
		// });

		if (state.isConnected && !showMini) {
			// console.log('🔄 [VoiceAgentDialog] Connected - minimizing first');
			onMinimize?.();
		} else {
			// console.log('🔄 [VoiceAgentDialog] Closing directly');
			onClose();
		}
	}, [state.isConnected, showMini, onMinimize, onClose]);

	// Close on escape key
	React.useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen) {
				console.log('Escape key pressed');
				handleClose();
			}
		};
		window.addEventListener('keydown', handleEscape);
		return () => window.removeEventListener('keydown', handleEscape);
	}, [handleClose, isOpen]);

	// Handle animation state
	React.useEffect(() => {
		if (isOpen) {
			setIsAnimating(true);
		} else {
			const timer = setTimeout(() => setIsAnimating(false), 300);
			return () => clearTimeout(timer);
		}
	}, [isOpen]);

	React.useEffect(() => {
		const handleMinimizeEvent = () => {
			// console.log('🎯 [VoiceAgentDialog] Minimize event received');
			onMinimize?.();
		};

		// Prevent any form submissions when clicking the Prometheus button
		const handleFormSubmit = (e: SubmitEvent) => {
			const target = e.target as HTMLElement;
			// Check if the form contains a Prometheus button
			if (target.querySelector('[data-prometheus-button]')) {
				// console.log(
				// 	'🛑 [VoiceAgentDialog] Preventing form submission from Prometheus button'
				// );
				e.preventDefault();
				e.stopPropagation();
				return false;
			}
		};

		// Add log for any external events that might be triggering state changes
		const handleGlobalClicks = (e: MouseEvent) => {
			const target = e.target as HTMLElement;
			const prometheusButton = target.closest('[data-prometheus-button]');
			if (prometheusButton) {
				// Prevent any default browser behavior
				e.preventDefault();
				e.stopPropagation();

				// console.log(
				// 	'🔍 [VoiceAgentDialog] Detected click on "Ask Prometheus" button',
				// 	{
				// 		isOpen,
				// 		showMini,
				// 		isConnected: state.isConnected,
				// 		timestamp: new Date().toISOString(),
				// 	}
				// );

				// Check if button is inside a form
				const parentForm = prometheusButton.closest('form');
				if (parentForm) {
					// console.log(
					// 	'⚠️ [VoiceAgentDialog] Prometheus button is inside a form - preventing submission'
					// );
					// Add a special handler to prevent form submission
					const originalOnSubmit = parentForm.onsubmit;
					parentForm.onsubmit = (e) => {
						e.preventDefault();
						e.stopPropagation();
						return false;
					};
				}

				// Flag to track first interaction (stored in window object to persist across renders)
				if (!window.__hasOpenedVoiceDialog) {
					window.__hasOpenedVoiceDialog = true;
					// console.log(
					// 	'🔍 [VoiceAgentDialog] First-time opening detected'
					// );
				}

				// Ensure the dialog state is handled properly
				if (isOpen && !showMini) {
					// console.log(
					// 	'🔄 [VoiceAgentDialog] Fixing dialog transition - moving to mini mode'
					// );
					// The natural transition should be to minimize, not hide completely
					onMinimize?.();
				}

				// Force visibility check after a short delay to ensure dialog is visible
				setTimeout(() => {
					// Check for any dialogs with improper visibility
					const dialogs = document.querySelectorAll(
						'[data-voice-dialog]'
					);
					dialogs.forEach((dialog) => {
						const computedStyle = window.getComputedStyle(dialog);
						// console.log(
						// 	'🔎 [VoiceAgentDialog] Dialog visibility check:',
						// 	{
						// 		visibility: computedStyle.visibility,
						// 		opacity: computedStyle.opacity,
						// 		display: computedStyle.display,
						// 	}
						// );

						// Force visibility if hidden
						if (
							computedStyle.visibility === 'hidden' ||
							computedStyle.opacity === '0'
						) {
							// console.log(
							// 	'🔧 [VoiceAgentDialog] Forcing dialog visibility'
							// );
							(dialog as HTMLElement).style.visibility =
								'visible';
							(dialog as HTMLElement).style.opacity = '1';
							(dialog as HTMLElement).style.display = 'flex';
						}
					});

					// Also check for backdrop visibility
					const backdrops = document.querySelectorAll(
						'.fixed.inset-0.bg-black\\/60'
					);
					backdrops.forEach((backdrop) => {
						const computedStyle = window.getComputedStyle(backdrop);
						// console.log(
						// 	'🔎 [VoiceAgentDialog] Backdrop visibility check:',
						// 	{
						// 		visibility: computedStyle.visibility,
						// 		opacity: computedStyle.opacity,
						// 		display: computedStyle.display,
						// 	}
						// );

						// Force visibility if hidden
						if (
							computedStyle.visibility === 'hidden' ||
							computedStyle.opacity === '0'
						) {
							// console.log(
							// 	'🔧 [VoiceAgentDialog] Forcing backdrop visibility'
							// );
							(backdrop as HTMLElement).style.visibility =
								'visible';
							(backdrop as HTMLElement).style.opacity = '0.6'; // Match the bg-black/60 class
							(backdrop as HTMLElement).style.display = 'block';
						}
					});
				}, 100);
			}
		};

		document.addEventListener('minimize-voice-dialog', handleMinimizeEvent);
		document.addEventListener('click', handleGlobalClicks, true);
		document.addEventListener('submit', handleFormSubmit, true);

		return () => {
			document.removeEventListener(
				'minimize-voice-dialog',
				handleMinimizeEvent
			);
			document.removeEventListener('click', handleGlobalClicks, true);
			document.removeEventListener('submit', handleFormSubmit, true);
		};
	}, [onMinimize, isOpen, showMini, state.isConnected]);

	// Add a visibility enforcement hook that runs periodically when dialog should be visible
	React.useEffect(() => {
		if (!isOpen) return;

		// Check visibility of dialog periodically for the first second after opening
		const visibilityChecks: NodeJS.Timeout[] = [];
		for (let i = 1; i <= 5; i++) {
			const timeoutId = setTimeout(() => {
				// Only run if still open
				if (!isOpen) return;

				const dialogs = document.querySelectorAll(
					'[data-voice-dialog], .fixed.inset-0.flex'
				);
				if (dialogs.length === 0) {
					// console.log(
					// 	'🔴 [VoiceAgentDialog] No dialog elements found to check visibility'
					// );
					return;
				}

				dialogs.forEach((dialog) => {
					const computedStyle = window.getComputedStyle(dialog);
					if (
						computedStyle.visibility === 'hidden' ||
						computedStyle.opacity === '0' ||
						computedStyle.display === 'none'
					) {
						// console.log(
						// 	'🔧 [VoiceAgentDialog] Correcting invisible dialog:',
						// 	{
						// 		element: dialog,
						// 		visibility: computedStyle.visibility,
						// 		opacity: computedStyle.opacity,
						// 		display: computedStyle.display,
						// 	}
						// );

						(dialog as HTMLElement).style.visibility = 'visible';
						(dialog as HTMLElement).style.opacity = '1';
						(dialog as HTMLElement).style.display = 'flex';
					}
				});

				// Also check for backdrop visibility
				const backdrops = document.querySelectorAll(
					'[data-voice-dialog-backdrop]'
				);
				backdrops.forEach((backdrop) => {
					const computedStyle = window.getComputedStyle(backdrop);

					if (
						computedStyle.visibility === 'hidden' ||
						parseFloat(computedStyle.opacity) < 0.1 ||
						computedStyle.display === 'none'
					) {
						(backdrop as HTMLElement).style.visibility = 'visible';
						(backdrop as HTMLElement).style.opacity = '0.6';
						(backdrop as HTMLElement).style.display = 'block';
					}
				});
			}, i * 200); // Check at 200ms, 400ms, 600ms, 800ms, 1000ms

			visibilityChecks.push(timeoutId);
		}

		return () => {
			visibilityChecks.forEach(clearTimeout);
		};
	}, [isOpen, showMini]);

	// Log when MiniVoiceAgentDialog should be rendered
	React.useEffect(() => {
		// console.log('🔍 [VoiceAgentDialog] Mini dialog rendering decision:', {
		// 	showMini,
		// 	isOpen,
		// 	isConnected: state.isConnected,
		// 	will_render: showMini && isOpen && state.isConnected,
		// 	timestamp: new Date().toISOString(),
		// });
	}, [showMini, isOpen, state.isConnected]);

	return (
		<>
			{/* Main dialog */}
			<AnimatePresence>
				{isOpen && !showMini && (
					<>
						{/* Backdrop */}
						<motion.div
							key='voice-backdrop'
							initial={
								clientSideInitialized
									? backdropInitialProps
									: { opacity: 0.6 }
							}
							animate={{ opacity: 0.6 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3 }}
							onClick={(e) => {
								console.log('Backdrop clicked');
								if (e.target === e.currentTarget) {
									handleClose();
								}
							}}
							className='fixed inset-0 bg-black/60 backdrop-blur-sm z-[200]'
							data-voice-dialog-backdrop='true'
						/>

						{/* Dialog */}
						<motion.div
							key='voice-dialog'
							{...(state.isConnected
								? { 'data-voice-dialog': true }
								: {})}
							initial={{ opacity: 0, scale: 0.95, y: 20 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							exit={{ opacity: 0, scale: 0.95, y: 20 }}
							transition={{ duration: 0.3 }}
							className='fixed inset-0 flex items-start justify-center z-[200]'
							data-voice-dialog-container='true'
						>
							<div className='w-[800px] h-[100vh] overflow-hidden flex flex-col -mt-20'>
								{/* Close button */}
								<motion.button
									// @ts-expect-error The 'type' attribute is a standard HTML attribute for buttons and should be permissible here.
									type='button'
									onClick={() => {
										console.log('Close button clicked');
										handleClose();
									}}
									whileHover={{ scale: 1.1 }}
									whileTap={{ scale: 0.95 }}
									className='absolute top-[333px] right-[605px] z-20 p-2 text-gray-50/80 dark:text-white/80 hover:text-gray-50 dark:hover:text-white bg-gradient-to-r from-purple-500/50 to-pink-600/50 hover:from-purple-500/60 hover:to-pink-600/60 backdrop-blur-sm rounded-full transition-all duration-200 shadow-[0_0_15px_0px_rgba(236,72,153,0.3)] hover:shadow-[0_0_20px_5px_rgba(236,72,153,0.4)]'
								>
									<motion.svg
										width='24'
										height='24'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='2'
										className='opacity-80 hover:opacity-100 transition-opacity'
										whileHover={{ rotate: 90 }}
										transition={{ duration: 0.2 }}
									>
										<path d='M18 6L6 18M6 6l12 12' />
									</motion.svg>
								</motion.button>

								{/* Content */}
								<div className='flex-1 relative'>
									<Canvas
										camera={{
											position: [0, 0, 15],
											fov: 45,
										}}
									>
										<React.Suspense fallback={null}>
											<Environment
												files='/assets/dikhololo_night_1k.hdr'
												background={false}
												blur={0.5 + volumeLevel * 0.6}
											/>
											<Sparkles
												count={22}
												scale={10}
												size={11}
												speed={0.3}
												color='#ffd700'
											/>
											<VoiceAgentVisualization
												volumeLevel={volumeLevel}
												isAssistantSpeaking={
													state.isAssistantSpeaking
												}
											/>
											<OrbitControls
												enableZoom={false}
												autoRotate
												autoRotateSpeed={1}
												target={[0, 0, 0]}
											/>
										</React.Suspense>
									</Canvas>

									<VoiceControls
										onVolumeChange={setVolumeLevel}
										onSpeakingStateChange={(
											isSpeaking: boolean
										) => {
											// console.log(
											// 	'🔍 [VoiceAgentDialog] Speaking state change:',
											// 	{
											// 		isSpeaking,
											// 		timestamp:
											// 			new Date().toISOString(),
											// 	}
											// );
											// eslint-disable-next-line @typescript-eslint/no-unused-expressions
											onSpeakingStateChange?.(
												isSpeaking
											) || void 0;
										}}
										onOpenChange={handleConnectionChange}
										isMinimized={showMini}
										userData={fetchedUserData}
										onNavigationStart={navigationStart}
										minimizeDialogRef={minimizeDialogRef}
										onMinimize={onMinimize}
										handleClose={handleClose}
									/>
								</div>
							</div>
						</motion.div>
					</>
				)}
			</AnimatePresence>

			{/* Mini dialog */}
			<AnimatePresence
				onExitComplete={() => {
					// console.log(
					// 	'🔍 [VoiceAgentDialog] Mini dialog exit animation completed',
					// 	{
					// 		timestamp: new Date().toISOString(),
					// 	}
					// );
				}}
			>
				{showMini && (
					<MiniVoiceAgentDialog
						onMaximize={() => {
							// console.log(
							// 	'🔍 [VoiceAgentDialog] Mini dialog maximize requested',
							// 	{
							// 		timestamp: new Date().toISOString(),
							// 	}
							// );
							// eslint-disable-next-line @typescript-eslint/no-unused-expressions
							onMaximize?.() || undefined;
						}}
						onVolumeChange={setVolumeLevel}
						onSpeakingStateChange={(isSpeaking: boolean) => {
							// console.log(
							// 	'🔍 [VoiceAgentDialog] Mini dialog speaking state change:',
							// 	{
							// 		isSpeaking,
							// 		timestamp: new Date().toISOString(),
							// 	}
							// );
							// eslint-disable-next-line @typescript-eslint/no-unused-expressions
							onSpeakingStateChange?.(isSpeaking) || void 0;
						}}
						isAssistantSpeaking={state.isAssistantSpeaking}
						isConnected={state.isConnected}
						setIsConnected={(connected) => {
							// console.log(
							// 	'🔍 [VoiceAgentDialog] Mini dialog connection state changing to:',
							// 	{
							// 		connected,
							// 		previousState: state.isConnected,
							// 		timestamp: new Date().toISOString(),
							// 	}
							// );
							voiceActions.setConnected(connected);
						}}
						userData={fetchedUserData}
						isOpen={showMini}
						onClose={handleClose}
						volumeLevel={volumeLevel}
					/>
				)}
			</AnimatePresence>
		</>
	);
}
